<!-- ConditionFormModal.vue -->
<template>
  <b-modal :visible="show" dialog-class="condition-modal" :ok-title="$t('composite_alarm_rule.save')"
    :cancel-title="$t('activation_condition.cancel')" @ok="handleSave" @hidden="resetForm"
    @change="(val) => $emit('change', val)">

    <template #modal-title>
      <span class="text-white">{{ $t("activation_condition.title") }}</span>
    </template>

    <h5 class="mb-1">{{ $t("activation_condition.devices") }}</h5>

    <b-form-checkbox v-model="isGranular" class="mb-2">
      {{ $t("activation_condition.granular_condition") }}
    </b-form-checkbox>

    <b-alert :show="needsPanel" variant="warning" class="p-1">
      {{ $t("activation_condition.needs_panel") }}
    </b-alert>

    <b-row v-if="isGranular">
      <b-col md="3">
        <b-form-group :label="$t('activation_condition.device_type')">
          <b-form-select v-model="deviceType" :options="deviceTypeOptions" :disabled="!idDmpPanel"
            @change="handleDeviceTypeChange" />
        </b-form-group>
      </b-col>

      <b-col md="3" v-if="deviceType === 'area_zone'">
        <b-form-group :label="$t('activation_condition.area')">
          <b-form-select v-model="area" :options="areaOptions" @change="handleAreaChange" />
        </b-form-group>
      </b-col>

      <b-col md="3" v-if="deviceType === 'area_zone'">
        <b-form-group :label="$t('activation_condition.zone')">
          <b-form-select v-model="device" :options="zoneOptionsForArea" :disabled="!area"
            @change="handleDeviceChange" />
          <small v-if="area && !zones.length" class="text-muted">
            {{ $t("activation_condition.area_without_zones") }}
          </small>
        </b-form-group>
      </b-col>

      <b-col md="3" v-else>
        <b-form-group :label="$t('activation_condition.device')">
          <b-form-select v-model="device" :options="deviceOptionsForType" :disabled="!deviceType"
            @change="handleDeviceChange" />
        </b-form-group>
      </b-col>

      <b-col md="3">
        <b-form-group :label="$t('activation_condition.device_tag')">
          <b-form-input :value="deviceTag" readonly :placeholder="$t('activation_condition.device_tag_placeholder')" />
        </b-form-group>
      </b-col>
    </b-row>

    <hr />

    <h5 class="mb-1">{{ $t("activation_condition.base_alarms") }}</h5>

    <b-row class="align-items-end">
      <b-col md="4">
        <b-form-group :label="$t('activation_condition.alarm_type')" class="mb-0">
          <small class="text-muted">
            {{ isGranular
              ? $t("activation_condition.alarm_type_hint_granular")
              : $t("activation_condition.alarm_type_hint_general") }}
          </small>

          <b-form-select v-model="pendingAlarmType" :options="alarmTypeOptions" />
        </b-form-group>
      </b-col>

      <b-col md="3">
        <b-form-group :label="$t('activation_condition.ocurrencies')" class="mb-0">
          <small class="text-muted">{{ $t("activation_condition.occurrences_hint") }}</small>
          <b-form-input v-model.number="minOccurrences" type="number" min="1"
            :placeholder="$t('activation_condition.ocurrencies')" />
        </b-form-group>
      </b-col>

      <b-col md="3">
        <b-button variant="primary" :disabled="!pendingAlarmType" @click="handleAddType">
          {{ $t("activation_condition.add") }}
        </b-button>
      </b-col>
    </b-row>

    <b-table v-if="typesList.length" :items="typesList" :fields="typesTableFields" small bordered responsive
      class="mt-2">
      <template #head()="data">
        {{ $t(data.label) }}
      </template>

      <template #head(actions)="">
        <span></span>
      </template>

      <template #cell(occurrences)="">
        {{ minOccurrences }}
      </template>

      <template #cell(actions)="{ index }">
        <b-button size="sm" variant="outline-danger" @click="handleRemoveType(index)">
          {{ $t("activation_condition.remove") }}
        </b-button>
      </template>
    </b-table>
  </b-modal>
</template>

<script>
import {
  BModal,
  BAlert,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BFormCheckbox,
  BButton,
  BTable,
} from "bootstrap-vue";
import store from "@/store";
import { ref, watch, computed, nextTick, onMounted } from "@vue/composition-api";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
  name: "ConditionFormModal",
  components: {
    BModal,
    BAlert,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BFormCheckbox,
    BButton,
    BTable,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    initialCondition: {
      type: Object,
      default: null,
    },
    idDmpPanel: {
      type: Number,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();

    const isGranular = ref(false);
    const deviceType = ref(null);
    const area = ref(null);
    const device = ref(null);
    const deviceTag = ref(null);
    const minOccurrences = ref(null);
    const pendingAlarmType = ref(null);
    const typesList = ref([]);

    const deviceTypeOptions = computed(() => [
      { value: null, text: t("composite_alarm_rule.select_type") },
      { value: "area_zone", text: t("activation_condition.device_types.area_zone") },
      { value: "zone24h", text: t("activation_condition.device_types.zone24h") },
      { value: "door", text: t("activation_condition.device_types.door") },
      { value: "output", text: t("activation_condition.device_types.output") },
    ]);

    const needsPanel = computed(() => isGranular.value && !props.idDmpPanel);

    const areas = computed(() => store.getters["dmp_panels/getPanelAreas"]);
    const zones = computed(() => store.getters["dmp_panels/getZones"]);
    const devices = computed(() => store.getters["dmp_panels/getDevices"]);
    const tagAlarmTypes = computed(() => store.getters["dmp_panels/getTagAlarmTypes"]);

    const toDeviceOption = (d) => ({
      value: d.id,
      text: d.id_device_tag ? d.name : `${d.name} (${t("activation_condition.without_tag")})`,
      disabled: !d.id_device_tag,
    });

    const areaOptions = computed(() => [
      { value: null, text: t("activation_condition.select_area") },
      ...areas.value.map((a) => ({ value: a.id, text: a.name })),
    ]);

    const zoneOptionsForArea = computed(() => [
      { value: null, text: t("activation_condition.select_zone") },
      ...zones.value.map(toDeviceOption),
    ]);

    const deviceOptionsForType = computed(() => [
      { value: null, text: t("activation_condition.select_device") },
      ...devices.value.map(toDeviceOption),
    ]);

    const alarmTypeOptions = computed(() => {
      if (isGranular.value) {
        return [
          { value: null, text: t("activation_condition.select_alarm_type") },
          ...tagAlarmTypes.value.map((t) => ({
            value: t.id,
            text: t.description,
            acronym: t.acronym,
          })),
        ];
      }

      const options = store.getters["alarm/getAlarmTypes"].map((option) => ({
        value: option.id,
        text: option.label,
        acronym: option.acronym,
      }));

      return [{ value: null, text: t("activation_condition.select_alarm_type") }, ...options];
    });

    const typesTableFields = [
      { key: "text", label: "activation_condition.alarm_type" },
      { key: "acronym", label: "activation_condition.acronym" },
      { key: "occurrences", label: "activation_condition.ocurrencies" },
      { key: "actions", label: "" },
    ];

    const clearTagSelection = () => {
      deviceTag.value = null;
      pendingAlarmType.value = null;
      typesList.value = [];
      store.commit("dmp_panels/setTagAlarmTypes", []);
    };

    const resetDeviceSelection = () => {
      deviceType.value = null;
      area.value = null;
      device.value = null;
      clearTagSelection();
      store.commit("dmp_panels/setAreas", []);
      store.commit("dmp_panels/setZones", []);
      store.commit("dmp_panels/setDevices", []);
    };

    const handleDeviceTypeChange = async () => {
      area.value = null;
      device.value = null;
      clearTagSelection();
      store.commit("dmp_panels/setZones", []);
      store.commit("dmp_panels/setDevices", []);

      if (!props.idDmpPanel || !deviceType.value) return;

      if (deviceType.value === "area_zone") {
        await store.dispatch("dmp_panels/getPanelAreas", props.idDmpPanel);
      } else {
        await store.dispatch("dmp_panels/getDevices", {
          idPanel: props.idDmpPanel,
          deviceType: deviceType.value,
        });
      }
    };

    const handleAreaChange = async () => {
      device.value = null;
      clearTagSelection();
      store.commit("dmp_panels/setZones", []);

      if (area.value) {
        await store.dispatch("dmp_panels/getZones", area.value);
      }
    };

    const handleDeviceChange = async () => {
      clearTagSelection();

      const list = deviceType.value === "area_zone" ? zones.value : devices.value;
      const found = list.find((d) => d.id === device.value);
      if (!found) return;

      deviceTag.value = found.tag_name;

      if (found.id_device_tag) {
        await store.dispatch("dmp_panels/getAlarmTypesByTag", found.id_device_tag);
      }
    };

    const handleAddType = () => {
      if (!pendingAlarmType.value) return;

      const alreadyAdded = typesList.value.some((t) => t.value === pendingAlarmType.value);
      if (alreadyAdded) {
        nextTick(() => { pendingAlarmType.value = null; });
        return;
      }

      const selected = alarmTypeOptions.value.find((o) => o.value === pendingAlarmType.value);
      if (!selected) return;

      typesList.value.push({
        value: selected.value,
        text: selected.text,
        acronym: selected.acronym,
      });

      nextTick(() => { pendingAlarmType.value = null; });
    };

    const handleRemoveType = (index) => {
      typesList.value.splice(index, 1);
    };

    const resetForm = () => {
      isGranular.value = false;
      minOccurrences.value = null;
      resetDeviceSelection();
    };

    const handleSave = (bvModalEvent) => {
      if (!typesList.value.length) {
        bvModalEvent.preventDefault();
        return;
      }

      if (!minOccurrences.value || minOccurrences.value < 1) {
        bvModalEvent.preventDefault();
        return;
      }

      const idDmpPanelArea = isGranular.value && deviceType.value === "area_zone" ? area.value : null;
      const idDmpPanelDevice = isGranular.value ? device.value : null;

      const isActuallyGranular = !!(idDmpPanelArea || idDmpPanelDevice);

      const deviceTypeLabel = isActuallyGranular
        ? (deviceTypeOptions.value.find((o) => o.value === deviceType.value) || {}).text
        : null;

      const areaLabel = isActuallyGranular && deviceType.value === "area_zone"
        ? (areaOptions.value.find((o) => o.value === area.value) || {}).text
        : null;

      const deviceLabel = isActuallyGranular
        ? (deviceType.value === "area_zone"
          ? (zoneOptionsForArea.value.find((o) => o.value === device.value) || {}).text
          : (deviceOptionsForType.value.find((o) => o.value === device.value) || {}).text)
        : null;

      const detailsLabel = [areaLabel, deviceLabel].filter(Boolean).join(" - ") || null;

      const payload = {
        id_dmp_panel_area: idDmpPanelArea,
        id_dmp_panel_device: idDmpPanelDevice,
        min_occurrences: minOccurrences.value,
        types: typesList.value.map((t) => t.value),
        _typesList: typesList.value.slice(),
        _display: {
          deviceTypeLabel,
          detailsLabel,
        },
        _expanded: false,
      };

      emit("save", payload);
    };

    watch(
      () => props.initialCondition,
      (condition) => {
        if (!condition) return;

        isGranular.value = !!(condition.id_dmp_panel_area || condition.id_dmp_panel_device);
        minOccurrences.value = condition.min_occurrences;
      }
    );

    watch(isGranular, () => {
      resetDeviceSelection();
    });

    watch(
      () => props.idDmpPanel,
      () => {
        resetDeviceSelection();
      }
    );

    onMounted(() => {
      if (!store.getters["alarm/getAlarmTypes"].length) {
        store.dispatch("alarm/getAlarmTypes");
      }
    });

    return {
      isGranular,
      deviceType,
      area,
      device,
      deviceTag,
      minOccurrences,
      pendingAlarmType,
      typesList,
      needsPanel,
      zones,
      deviceTypeOptions,
      areaOptions,
      zoneOptionsForArea,
      deviceOptionsForType,
      alarmTypeOptions,
      typesTableFields,
      handleDeviceTypeChange,
      handleAreaChange,
      handleDeviceChange,
      handleAddType,
      handleRemoveType,
      handleSave,
      resetForm,
    };
  },
};
</script>

<style>
.condition-modal {
  max-width: 1400px;
}
</style>